import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Delete, Key, Add } from '@mui/icons-material';
import axios from 'axios';
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import { format } from 'date-fns';
import { useAuth } from '../../../util/authContext';
import { getSessionDek, wrapDekWithPrf, setPrfWrap, decodeBase64 } from '../../../util/keyManager';

const PasskeyManagementModal = ({ open, onClose }) => {
  const { data: userData } = useAuth();
  const [credentials, setCredentials] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Registration state
  const [isRegistering, setIsRegistering] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [showNameInput, setShowNameInput] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchCredentials = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await axios.get('/api/webauthn/credentials');
      setCredentials(res.data.credentials || []);
    } catch (err) {
      console.error('Failed to fetch passkeys:', err);
      setError('Failed to load passkeys');
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (open) {
      fetchCredentials();
      setShowNameInput(false);
      setDeviceName('');
      setSuccess(null);
      setError(null);
    }
  }, [open, fetchCredentials]);

  const handleStartRegistration = () => {
    setShowNameInput(true);
    setDeviceName('');
    setError(null);
    setSuccess(null);
  };

  const handleRegisterPasskey = async () => {
    if (!deviceName.trim()) {
      setError('Please enter a name for this passkey');
      return;
    }

    setIsRegistering(true);
    setError(null);
    setSuccess(null);

    try {
      // Step 1: Get registration options from server
      const optionsRes = await axios.get('/api/webauthn/register/options');
      const options = optionsRes.data;

      // Step 2: Start WebAuthn registration (browser prompt)
      const registrationResponse = await startRegistration({ optionsJSON: options });

      // Step 3: Verify with server
      await axios.post('/api/webauthn/register/verify', {
        response: registrationResponse,
        deviceName: deviceName.trim(),
      });

      // Step 4: If the new passkey supports PRF, do an immediate PRF assertion
      // to wrap the DEK for offline unlock.
      if (registrationResponse.clientExtensionResults?.prf?.enabled) {
        try {
          const statusRes = await axios.get('/api/keys/status');
          const prfSalt = statusRes.data.prfSalt;
          if (prfSalt && userData?.email) {
            const authOptionsRes = await axios.post('/api/webauthn/auth/options', { email: userData.email });
            const authOptions = authOptionsRes.data;
            const prfSaltFirst = authOptions.extensions?.prf?.eval?.first;
            if (prfSaltFirst) {
              authOptions.extensions.prf.eval.first = decodeBase64(prfSaltFirst);
            }
            const authResponse = await startAuthentication({ optionsJSON: authOptions });
            const prfFirst = authResponse.clientExtensionResults?.prf?.results?.first;
            if (prfFirst) {
              const dek = getSessionDek();
              const wrap = await wrapDekWithPrf(dek, prfFirst, prfSalt);
              setPrfWrap(authResponse.id, wrap);
              axios.put(
                `/api/webauthn/credentials/${authResponse.id}/prf-wrap`,
                { prfWrappedDek: { ciphertext: wrap.ciphertext, iv: wrap.iv } },
                { withCredentials: true }
              ).catch(e => console.warn('Could not upload PRF wrap (non-fatal):', e));
            }
          }
        } catch (prfErr) {
          // PRF wrap is best-effort; registration already succeeded
          console.warn('PRF wrap after registration failed (non-fatal):', prfErr);
        }
      }

      setSuccess('Passkey registered successfully!');
      setShowNameInput(false);
      setDeviceName('');
      fetchCredentials();
    } catch (err) {
      console.error('Passkey registration error:', err);
      
      // Handle WebAuthn browser errors
      if (err.name === 'NotAllowedError') {
        setError('Registration was cancelled or timed out. Please try again.');
      } else if (err.name === 'InvalidStateError') {
        setError('This passkey is already registered.');
      } else if (err.name === 'SecurityError') {
        setError('Security error. Please ensure you are using HTTPS.');
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError(err.message || 'Failed to register passkey');
      }
    }

    setIsRegistering(false);
  };

  const handleDeletePasskey = async (credentialId, name) => {
    if (!window.confirm(`Are you sure you want to delete the passkey "${name}"?`)) {
      return;
    }

    setError(null);
    setSuccess(null);
    setDeletingId(credentialId);

    try {
      await axios.delete('/api/webauthn/credentials', {
        data: { credentialId },
      });
      setSuccess('Passkey deleted successfully');
      fetchCredentials();
    } catch (err) {
      console.error('Failed to delete passkey:', err);
      setError(err.response?.data?.error || 'Failed to delete passkey');
    }
    setDeletingId(null);
  };

  const handleClose = () => {
    setShowNameInput(false);
    setDeviceName('');
    setError(null);
    setSuccess(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Key />
          Manage Passkeys
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body2" color="textSecondary" paragraph>
          Passkeys let you sign in securely using Face ID, Touch ID, Windows Hello, or a security key — no password needed.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {credentials.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                You don't have any passkeys registered yet. Add one to enable passwordless sign-in.
              </Alert>
            ) : (
              <List>
                {credentials.map((cred, index) => (
                  <React.Fragment key={cred.credentialId}>
                    {index > 0 && <Divider />}
                    <ListItem>
                      <ListItemText
                        primary={cred.deviceName || 'Unnamed Passkey'}
                        secondary={
                          <>
                            {cred.createdAt && !isNaN(new Date(cred.createdAt).getTime()) && (
                              <span>
                                Added {format(new Date(cred.createdAt), 'MMM d, yyyy')}
                              </span>
                            )}
                            {cred.lastUsedAt && !isNaN(new Date(cred.lastUsedAt).getTime()) && (
                              <span>
                                {' • '}Last used {format(new Date(cred.lastUsedAt), 'MMM d, yyyy')}
                              </span>
                            )}
                          </>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => handleDeletePasskey(cred.credentialId, cred.deviceName)}
                          color="error"
                          disabled={deletingId === cred.credentialId}
                        >
                          {deletingId === cred.credentialId ? <CircularProgress size={20} /> : <Delete />}
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}

            <Divider sx={{ my: 2 }} />

            {showNameInput ? (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Name your passkey
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Give this passkey a name to help you identify it later (e.g., "MacBook Pro", "iPhone", "YubiKey").
                </Typography>
                <TextField
                  autoFocus
                  fullWidth
                  label="Passkey Name"
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                  placeholder="e.g., MacBook Pro"
                  disabled={isRegistering}
                  sx={{ mb: 2 }}
                />
                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleRegisterPasskey}
                    disabled={isRegistering || !deviceName.trim()}
                    startIcon={isRegistering ? <CircularProgress size={20} /> : <Key />}
                  >
                    {isRegistering ? 'Registering...' : 'Register Passkey'}
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => {
                      setShowNameInput(false);
                      setDeviceName('');
                      setError(null);
                    }}
                    disabled={isRegistering}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<Add />}
                onClick={handleStartRegistration}
                fullWidth
              >
                Add a Passkey
              </Button>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PasskeyManagementModal;
